/* eslint-disable indent */
import {
  Grid,
  makeStyles,
  Typography,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import i18n from 'i18n';
import Loading from 'components/Loading';
import Permission from 'components/ValidPermission';
import { getAdvisorRequest } from 'modules/advisors/actions';
import { Input as InputIcon } from '@material-ui/icons';
import CloudIcon from '@material-ui/icons/Cloud';
import {
  AdvisorDetailsProps,
  AdvisorTypeEnum,
  AdvisorEnvironmentBehavior,
} from 'modules/advisors/interfaces';
import { useHistory } from 'react-router';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import { selectCompany, selectPartner } from 'modules/authenticate/actions';
import {
  CompanyData,
  PartnerData,
  PermissionsData,
} from 'modules/authenticate/interfaces';
import { modalClose } from 'modules/modal/actions';
import { getCompanyName } from 'utils/tsHelpers';
import { formatCurrency, isEmptyObj } from 'utils/helpers';
import styles from '../styles';
import BehaviorDetails from './behaviorDetails';
import AdvisorStatusInfo from './AdvisorStatusInfo';
import AdvisorEnvironmentSessionsGraph from './AdvisorEnvironmentSessionsGraph';

const useStyles = makeStyles(styles);

interface Props {
  advisorUuid: string;
  advisorDetails: AdvisorDetailsProps | null;
  isLoading: boolean;
  permissions?: PermissionsData;
  getAdvisorRequest: (advisorUuid: string) => void;
  selectCompany: (company: CompanyData | null) => void;
  selectPartner: (partner: PartnerData | null) => void;
  modalClose: () => void;
  getTranslatedImpact: (impact?: string) => string | JSX.Element | undefined;
}

const AdvisorDetails = ({
  advisorUuid,
  advisorDetails,
  isLoading,
  permissions,
  getAdvisorRequest,
  selectCompany,
  selectPartner,
  modalClose,
  getTranslatedImpact,
}: Props) => {
  const classes = useStyles();
  const history = useHistory();

  const getHighlight = (
    current: Record<string, any>,
    suggested: Record<string, any>
  ) => {
    const keys = Object.keys(current);
    const highlights: string[] = [];

    keys.forEach(key => {
      if (current[key] !== suggested[key]) {
        highlights.push(key);
      }
    });

    return highlights;
  };

  React.useEffect(() => {
    getAdvisorRequest(advisorUuid);
  }, [getAdvisorRequest, advisorUuid]);

  const mapAdvisorType = (status: AdvisorTypeEnum | string) => {
    const advisorTypeMap = {
      [AdvisorTypeEnum.WEEKDAY]: 'week',
      [AdvisorTypeEnum.SUNDAY]: 'sunday',
      [AdvisorTypeEnum.SATURDAY]: 'saturday',
      [AdvisorTypeEnum.DYNAMIC_WEEKDAY_END]: 'week',
      [AdvisorTypeEnum.DYNAMIC_WEEKDAY_START]: 'week',
    };

    return advisorTypeMap[status];
  };

  const getTitleByAdvisorType = (advisorType: AdvisorTypeEnum) => {
    const advisorTitleMap = {
      [AdvisorTypeEnum.WEEKDAY]: i18n.t('EMPTY_INSTANCES_WEEKDAY_KEY'),
      [AdvisorTypeEnum.SUNDAY]: i18n.t('EMPTY_INSTANCES_SUNDAY_KEY'),
      [AdvisorTypeEnum.SATURDAY]: i18n.t('EMPTY_INSTANCES_SATURDAY_KEY'),
      [AdvisorTypeEnum.DYNAMIC_WEEKDAY_END]: i18n.t(
        'EMPTY_INSTANCES_WEEKDAY_KEY'
      ),
      [AdvisorTypeEnum.DYNAMIC_WEEKDAY_START]: i18n.t(
        'EMPTY_INSTANCES_WEEKDAY_KEY'
      ),
    };
    return advisorTitleMap[advisorType];
  };

  const redirectToContext = (item: AdvisorDetailsProps) => {
    if (
      item?.company &&
      item?.company?.company_uuid &&
      item?.partner?.partner_name &&
      item?.partner?.partner_uuid
    ) {
      selectCompany({
        id: item?.company?.company_uuid,
        name: getCompanyName(item.company),
        shortName: getCompanyName(item.company),
      });
      selectPartner({
        id: item?.partner?.partner_uuid,
        name: item?.partner?.partner_name,
        shortName: item?.partner?.partner_name,
      });
      history.push(
        `/environments/${item?.environment?.environment_uuid}/configuration`
      );
    }
  };

  const getDetailsState = (): {
    firstState?: AdvisorEnvironmentBehavior;
    firstStateTitle?: string;
    secondState?: AdvisorEnvironmentBehavior;
    secondStateTitle?: string;
  } => {
    if (
      advisorDetails?.status &&
      ['ignored', 'expired'].includes(advisorDetails?.status)
    )
      return {};

    const resolveState = (value?: AdvisorEnvironmentBehavior) =>
      isEmptyObj(value) ? undefined : value;

    if (advisorDetails?.status === 'pending') {
      return {
        firstState: resolveState(
          advisorDetails?.environment?.behavior?.current
        ),
        firstStateTitle: i18n.t('CURRENT_KEY'),
        secondState: resolveState(
          advisorDetails?.environment?.behavior?.suggested
        ),
        secondStateTitle: i18n.t('SUGGESTED_KEY'),
      };
    }

    if (
      advisorDetails?.status === 'applied' ||
      advisorDetails?.status === 'reverted'
    ) {
      return {
        firstState: resolveState(
          advisorDetails?.environment?.behavior?.previous
        ),
        firstStateTitle: i18n.t('PREVIOUS_KEY'),
        secondState: resolveState(
          advisorDetails?.environment?.behavior?.applied
        ),
        secondStateTitle: i18n.t('APPLIED_KEY'),
      };
    }

    return {};
  };

  const {
    firstState,
    firstStateTitle,
    secondState,
    secondStateTitle,
  } = getDetailsState();

  return isLoading || !advisorDetails ? (
    <Loading />
  ) : (
    <>
      <Grid container spacing={2} className={classes.advisorDetails}>
        <Grid container className={classes.header}>
          <Grid container className={classes.containerTitle}>
            <CloudIcon className={classes.personIconGray}></CloudIcon>
            <Typography
              className={classes.cardName}
              onClick={() => {
                if (permissions?.edit_environment) {
                  modalClose();
                  redirectToContext(advisorDetails);
                }
              }}
            >
              {advisorDetails?.environment?.environment_name}
            </Typography>
            <Permission value="edit_environment">
              <Tooltip
                title={i18n.t('ACCESS_ENVIRONMENT_KEY')}
                arrow
                placement="right"
              >
                <IconButton
                  className={classes.accessButton}
                  onClick={() => {
                    modalClose();
                    redirectToContext(advisorDetails);
                  }}
                >
                  <InputIcon />
                </IconButton>
              </Tooltip>
            </Permission>
          </Grid>
          <Grid className={classes.responsiveGrid}>
            <Grid container xs={4}>
              <Grid container xs={12} className={classes.cardInfos}>
                <Typography className={classes.cardTitles}>
                  {i18n.t('COMPANY_NAME_KEY')}
                  {': '}
                </Typography>
                <Typography className={classes.cardValues}>
                  {advisorDetails?.company?.company_name}
                </Typography>
              </Grid>
              <Grid container xs={12} className={classes.cardInfos}>
                <Typography className={classes.cardTitles}>
                  {i18n.t('PARTNER_NAME_KEY')}
                  {': '}
                </Typography>
                <Typography className={classes.cardValues}>
                  {advisorDetails?.partner?.partner_name}
                </Typography>
              </Grid>
              <Grid container xs={12} className={classes.cardInfos}>
                <Typography className={classes.cardTitles}>
                  {i18n.t('ENVIRONMENT_NAME_KEY')}
                  {': '}
                </Typography>
                <Typography className={classes.cardValues}>
                  {advisorDetails?.environment?.environment_name}
                </Typography>
              </Grid>
            </Grid>
            <Grid container xs={4} className={classes.cardContent}>
              <Grid container xs={12} className={classes.cardInfos}>
                <Typography className={classes.cardTitles}>
                  {i18n.t('BEHAVIOR_KEY')}
                  {': '}
                </Typography>
                <Typography className={classes.cardValues}>
                  {i18n.t(
                    `ADVISOR_PAGE.${advisorDetails?.environment?.environment_type?.toUpperCase()}_KEY`
                  )}
                </Typography>
              </Grid>
              <Grid container xs={12} className={classes.cardInfos}>
                <Typography className={classes.cardTitles}>
                  {i18n.t('LICENSES_KEY')}
                  {': '}
                </Typography>
                <Typography className={classes.cardValues}>
                  {advisorDetails?.environment?.environment_licenses || '0'}
                </Typography>
              </Grid>
              <Grid container xs={12} className={classes.cardInfos}>
                <Typography className={classes.cardTitles}>
                  {i18n.t('TIMEZONE_KEY')}
                  {': '}
                </Typography>
                <Typography className={classes.cardValues}>
                  {advisorDetails?.environment?.environment_timezone || '-'}
                </Typography>
              </Grid>
            </Grid>
            <Grid container xs={4} className={classes.cardContent}>
              <Grid container xs={12} className={classes.cardInfos}>
                <Typography className={classes.cardTitles}>
                  {i18n.t('ADVISOR_PAGE.ADVISOR_TYPE_DETAILS_KEY')}
                  {': '}
                </Typography>
                <Typography className={classes.cardValues}>
                  {i18n.t(`${advisorDetails?.advisor_type?.toUpperCase()}_KEY`)}
                </Typography>
              </Grid>
              <Grid container xs={12} className={classes.cardInfos}>
                <Typography className={classes.cardTitles}>
                  {i18n.t('STATUS_KEY')}
                  {': '}
                </Typography>
                <Typography className={classes.cardValues}>
                  {i18n.t(`${advisorDetails?.status?.toUpperCase()}_KEY`)}
                </Typography>
              </Grid>
              <Grid container xs={12} className={classes.cardInfos}>
                <Typography className={classes.cardTitles}>
                  {i18n.t('ADVISOR_PAGE.ADVISOR_MONTHLY_SAVINGS_KEY')}
                  {': '}
                </Typography>
                <Typography className={classes.cardValues}>
                  {formatCurrency(
                    advisorDetails?.estimated_monthly_cost_savings
                  )}
                </Typography>
              </Grid>
              <Grid container xs={12} className={classes.cardInfos}>
                <Typography className={classes.cardTitles}>
                  {i18n.t('RISK_KEY')}
                  {': '}
                </Typography>
                <Typography className={classes.cardValues}>
                  {getTranslatedImpact(advisorDetails?.impact) || '-'}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {advisorDetails?.status === 'pending' &&
          permissions?.list_environment_dashboard && (
            <AdvisorEnvironmentSessionsGraph
              environmentUuid={advisorDetails?.environment?.environment_uuid}
            />
          )}

        <AdvisorStatusInfo advisorDetails={advisorDetails} />

        {['pending', 'applied', 'reverted'].includes(advisorDetails?.status) &&
          advisorDetails?.advisor_type !== 'empty_instances_any_day' &&
          firstState &&
          secondState && (
            <Grid container className={classes.behaviorBorder}>
              <Grid container alignItems="center" justify="center">
                <Typography className={classes.behaviorTitle}>
                  {getTitleByAdvisorType(advisorDetails?.advisor_type)}
                </Typography>
              </Grid>
              <Grid
                container
                item
                spacing={2}
                xs={12}
                className={classes.contentDetails}
              >
                <Grid item xs={6}>
                  <Grid item xs={12}>
                    <Typography variant="h6" className={classes.colTitle}>
                      {`${firstStateTitle}:`}
                    </Typography>
                  </Grid>

                  <BehaviorDetails
                    title={i18n.t('FIRST_PERIOD_KEY')}
                    data={
                      firstState[mapAdvisorType(advisorDetails?.advisor_type)]
                        ?.first_period
                    }
                    environmentType={
                      advisorDetails?.environment?.environment_type
                    }
                  />

                  <BehaviorDetails
                    title={i18n.t('SECOND_PERIOD_KEY')}
                    data={
                      firstState[mapAdvisorType(advisorDetails?.advisor_type)]
                        ?.second_period
                    }
                    environmentType={
                      advisorDetails?.environment?.environment_type
                    }
                  />
                </Grid>

                <Grid item xs={6} className={classes.colSuggested}>
                  <Grid item xs={12}>
                    <Typography variant="h6" className={classes.colTitle}>
                      {`${secondStateTitle}:`}
                    </Typography>
                  </Grid>

                  <BehaviorDetails
                    title={i18n.t('FIRST_PERIOD_KEY')}
                    highlights={getHighlight(
                      firstState[mapAdvisorType(advisorDetails?.advisor_type)]
                        ?.first_period,
                      secondState[mapAdvisorType(advisorDetails?.advisor_type)]
                        ?.first_period
                    )}
                    data={
                      secondState[mapAdvisorType(advisorDetails?.advisor_type)]
                        ?.first_period
                    }
                    environmentType={
                      advisorDetails?.environment?.environment_type
                    }
                  />

                  <BehaviorDetails
                    title={i18n.t('SECOND_PERIOD_KEY')}
                    highlights={getHighlight(
                      firstState[mapAdvisorType(advisorDetails?.advisor_type)]
                        .second_period,
                      secondState[mapAdvisorType(advisorDetails?.advisor_type)]
                        .second_period
                    )}
                    data={
                      secondState[mapAdvisorType(advisorDetails?.advisor_type)]
                        .second_period
                    }
                    environmentType={
                      advisorDetails?.environment?.environment_type
                    }
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
        {['pending', 'applied', 'reverted'].includes(advisorDetails?.status) &&
          advisorDetails?.advisor_type === 'empty_instances_any_day' &&
          firstState &&
          secondState && (
            <>
              <Grid container className={classes.behaviorBorder}>
                <Grid container alignItems="center" justify="center">
                  <Typography className={classes.behaviorTitle}>
                    {i18n.t('EMPTY_INSTANCES_WEEKDAY_KEY')}
                  </Typography>
                </Grid>
                <Grid
                  container
                  item
                  spacing={2}
                  xs={12}
                  className={classes.contentDetails}
                >
                  <Grid item xs={6}>
                    <Grid item xs={12}>
                      <Typography variant="h6" className={classes.colTitle}>
                        {`${firstStateTitle}:`}
                      </Typography>
                    </Grid>

                    <BehaviorDetails
                      title={i18n.t('FIRST_PERIOD_KEY')}
                      data={firstState?.week?.first_period}
                      environmentType={
                        advisorDetails?.environment?.environment_type
                      }
                    />

                    <BehaviorDetails
                      title={i18n.t('SECOND_PERIOD_KEY')}
                      data={firstState?.week?.second_period}
                      environmentType={
                        advisorDetails?.environment?.environment_type
                      }
                    />
                  </Grid>

                  <Grid item xs={6} className={classes.colSuggested}>
                    <Grid item xs={12}>
                      <Typography variant="h6" className={classes.colTitle}>
                        {`${secondStateTitle}:`}
                      </Typography>
                    </Grid>

                    <BehaviorDetails
                      title={i18n.t('FIRST_PERIOD_KEY')}
                      highlights={getHighlight(
                        firstState?.week?.first_period,
                        secondState?.week?.first_period
                      )}
                      data={secondState?.week?.first_period}
                      environmentType={
                        advisorDetails?.environment?.environment_type
                      }
                    />

                    <BehaviorDetails
                      title={i18n.t('SECOND_PERIOD_KEY')}
                      highlights={getHighlight(
                        firstState?.week.second_period,
                        secondState?.week.second_period
                      )}
                      data={secondState?.week.second_period}
                      environmentType={
                        advisorDetails?.environment?.environment_type
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid container className={classes.behaviorBorder}>
                <Grid container alignItems="center" justify="center">
                  <Typography className={classes.behaviorTitle}>
                    {i18n.t('EMPTY_INSTANCES_SATURDAY_KEY')}
                  </Typography>
                </Grid>
                <Grid
                  container
                  item
                  spacing={2}
                  xs={12}
                  className={classes.contentDetails}
                >
                  <Grid item xs={6}>
                    <Grid item xs={12}>
                      <Typography variant="h6" className={classes.colTitle}>
                        {`${firstStateTitle}:`}
                      </Typography>
                    </Grid>

                    <BehaviorDetails
                      title={i18n.t('FIRST_PERIOD_KEY')}
                      data={firstState?.saturday?.first_period}
                      environmentType={
                        advisorDetails?.environment?.environment_type
                      }
                    />

                    <BehaviorDetails
                      title={i18n.t('SECOND_PERIOD_KEY')}
                      data={firstState?.saturday?.second_period}
                      environmentType={
                        advisorDetails?.environment?.environment_type
                      }
                    />
                  </Grid>

                  <Grid item xs={6} className={classes.colSuggested}>
                    <Grid item xs={12}>
                      <Typography variant="h6" className={classes.colTitle}>
                        {`${secondStateTitle}:`}
                      </Typography>
                    </Grid>

                    <BehaviorDetails
                      title={i18n.t('FIRST_PERIOD_KEY')}
                      highlights={getHighlight(
                        firstState?.saturday?.first_period,
                        secondState?.saturday?.first_period
                      )}
                      data={secondState?.saturday?.first_period}
                      environmentType={
                        advisorDetails?.environment?.environment_type
                      }
                    />

                    <BehaviorDetails
                      title={i18n.t('SECOND_PERIOD_KEY')}
                      highlights={getHighlight(
                        firstState?.saturday.second_period,
                        secondState?.saturday.second_period
                      )}
                      data={secondState?.saturday.second_period}
                      environmentType={
                        advisorDetails?.environment?.environment_type
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid container className={classes.behaviorBorder}>
                <Grid container alignItems="center" justify="center">
                  <Typography className={classes.behaviorTitle}>
                    {i18n.t('EMPTY_INSTANCES_SUNDAY_KEY')}
                  </Typography>
                </Grid>
                <Grid
                  container
                  item
                  spacing={2}
                  xs={12}
                  className={classes.contentDetails}
                >
                  <Grid item xs={6}>
                    <Grid item xs={12}>
                      <Typography variant="h6" className={classes.colTitle}>
                        {`${firstStateTitle}:`}
                      </Typography>
                    </Grid>

                    <BehaviorDetails
                      title={i18n.t('FIRST_PERIOD_KEY')}
                      data={firstState?.sunday?.first_period}
                      environmentType={
                        advisorDetails?.environment?.environment_type
                      }
                    />

                    <BehaviorDetails
                      title={i18n.t('SECOND_PERIOD_KEY')}
                      data={firstState?.sunday?.second_period}
                      environmentType={
                        advisorDetails?.environment?.environment_type
                      }
                    />
                  </Grid>

                  <Grid item xs={6} className={classes.colSuggested}>
                    <Grid item xs={12}>
                      <Typography variant="h6" className={classes.colTitle}>
                        {`${secondStateTitle}:`}
                      </Typography>
                    </Grid>

                    <BehaviorDetails
                      title={i18n.t('FIRST_PERIOD_KEY')}
                      highlights={getHighlight(
                        firstState?.sunday?.first_period,
                        secondState?.sunday?.first_period
                      )}
                      data={secondState?.sunday?.first_period}
                      environmentType={
                        advisorDetails?.environment?.environment_type
                      }
                    />

                    <BehaviorDetails
                      title={i18n.t('SECOND_PERIOD_KEY')}
                      highlights={getHighlight(
                        firstState?.sunday.second_period,
                        secondState?.sunday.second_period
                      )}
                      data={secondState?.sunday.second_period}
                      environmentType={
                        advisorDetails?.environment?.environment_type
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
            </>
          )}
      </Grid>
    </>
  );
};

const mapStateToProps = ({
  authenticate: { data },
  advisors: { isLoading, advisorDetails },
}: ApplicationState) => ({
  isLoading,
  advisorDetails,
  permissions: data?.permissions,
});

export default connect(mapStateToProps, {
  getAdvisorRequest,
  selectCompany,
  selectPartner,
  modalClose,
})(AdvisorDetails);
